/**
 * Session state.
 *
 * On mount the app attempts a silent refresh: the httpOnly cookie is the durable part
 * of the session, so a page reload restores the user without a login prompt while the
 * access token itself never leaves memory.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { can, type Permission, type UserDto } from '@ias/shared';

import { post, setAccessToken, setUnauthenticatedHandler } from './api/client';
import { purgePersistedCache } from './offline/persister';

interface AuthValue {
  user: UserDto | null;
  /** Distinguishes "still checking the cookie" from "definitely signed out". */
  initialising: boolean;
  signIn: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  /** Permission check against the shared role matrix — same rules the API enforces. */
  can: (permission: Permission) => boolean;
}

const AuthContext = React.createContext<AuthValue | null>(null);

interface Session {
  user: UserDto;
  accessToken: string;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<UserDto | null>(null);
  const [initialising, setInitialising] = React.useState(true);
  const queryClient = useQueryClient();

  const clearSession = React.useCallback(() => {
    setAccessToken(null);
    setUser(null);
    queryClient.clear();
    // Wipe the on-disk read cache too. Clearing only the in-memory client would leave
    // the previous user's book in IndexedDB for the next person at this machine.
    void purgePersistedCache();
  }, [queryClient]);

  React.useEffect(() => {
    setUnauthenticatedHandler(clearSession);
  }, [clearSession]);

  /**
   * Silent sign-in from the refresh cookie, exactly once per page load.
   *
   * The ref guard is what makes it once: StrictMode double-invokes mount effects in
   * development, and two rotations per load is wasted work. Deliberately *no*
   * cancellation flag — the cleanup between StrictMode's two passes would cancel the
   * only in-flight request, and `initialising` would never clear, leaving the app
   * stuck on the boot splash forever.
   */
  const bootstrapped = React.useRef(false);

  React.useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    (async () => {
      try {
        const session = await post<Session>('/auth/refresh');
        setAccessToken(session.accessToken);
        setUser(session.user);
      } catch {
        // No valid cookie — an ordinary signed-out load, not an error worth surfacing.
      } finally {
        setInitialising(false);
      }
    })();
  }, []);

  const signIn = React.useCallback(
    async (email: string, password: string, rememberMe: boolean) => {
      const session = await post<Session>('/auth/login', { email, password, rememberMe });
      setAccessToken(session.accessToken);
      setUser(session.user);
    },
    [],
  );

  const signOut = React.useCallback(async () => {
    try {
      await post('/auth/logout');
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = React.useMemo<AuthValue>(
    () => ({
      user,
      initialising,
      signIn,
      signOut,
      can: (permission) => can(user?.role, permission),
    }),
    [user, initialising, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

/** Guard for conditional rendering: `{can('policy:write') && <Button/>}`. */
export function usePermission(permission: Permission): boolean {
  return useAuth().can(permission);
}

/** Hides its children unless the current role holds the permission. */
export function Protected({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  return usePermission(permission) ? <>{children}</> : <>{fallback}</>;
}
